// Canonical journey-row fixture builders (prompt-context integration
// contract): the composer's prompt readers, their tests, and the future
// lifecycle/wayfinder sides build stored-row shapes through these ONE set of
// builders, so a contract change breaks every consumer's tests in the same
// commit instead of drifting one hand-rolled literal at a time.
//
// Structural row shapes, declared here rather than imported from `db/schema`:
// the schema module types its jsonb columns FROM this seam (ADR-066 Rule 2),
// so importing it back would be circular — these mirror the tables' select
// shape and the schema tests hold the two in step.

import { tileCopySchema, type GenerationReason, type SourceTier, type TileCopy } from './schema';
import type { StoredPracticeContent } from './schema';
import { PRACTICE_OUTCOME_STATES, type PracticeOutcomeState } from './outcomes';

/** A `composed_practices` row as a Drizzle select returns it. */
export interface ComposedPracticeRowFixture {
  id: string;
  memberId: string;
  dayKey: string;
  batchId: string;
  generationReason: GenerationReason;
  practice: StoredPracticeContent;
  tileCopy: TileCopy;
  techniqueSlug: string;
  sourceTier: SourceTier;
  supersededAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

/** A `journey_day_plans` row as a Drizzle select returns it. */
export interface JourneyDayPlanRowFixture {
  id: string;
  memberId: string;
  dayKey: string;
  batchId: string;
  reason: GenerationReason;
  orderedPracticeIds: string[];
  requestId: string | null;
  supersededAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export const FIXTURE_MEMBER_ID = '7b6a1c1e-2f4d-4a4b-8c3d-1e2f3a4b5c6d';
export const FIXTURE_DAY_KEY = '2026-08-20';

const FIXTURE_INSTANT = new Date('2026-08-20T06:00:00.000Z');

export function buildComposedPracticeRow(
  overrides: Partial<ComposedPracticeRowFixture> = {},
): ComposedPracticeRowFixture {
  const row: ComposedPracticeRowFixture = {
    id: '018f0000-0000-7000-8000-000000000001',
    memberId: FIXTURE_MEMBER_ID,
    dayKey: FIXTURE_DAY_KEY,
    batchId: '018f0000-0000-7000-8000-00000000b001',
    generationReason: 'nightly',
    practice: {
      status: 'built',
      practice_id: 'fixture-practice-1',
      technique: 'urge_surfing',
      technique_version: '1',
      intent: 'fixture intent sentence for the built arm',
      instructions: 'fixture practice body',
      never: 'fixture never clause',
      done_when: 'fixture done-when clause',
      first_move: null,
    },
    tileCopy: tileCopySchema.parse({
      title: 'Ride the urge',
      hook: 'A two-minute practice for the moment a craving peaks.',
    }),
    techniqueSlug: 'urge_surfing',
    sourceTier: 'generative',
    supersededAt: null,
    createdAt: FIXTURE_INSTANT,
    updatedAt: FIXTURE_INSTANT,
    ...overrides,
  };
  return row;
}

export function buildJourneyDayPlanRow(
  overrides: Partial<JourneyDayPlanRowFixture> = {},
): JourneyDayPlanRowFixture {
  return {
    id: '018f0000-0000-7000-8000-00000000d001',
    memberId: FIXTURE_MEMBER_ID,
    dayKey: FIXTURE_DAY_KEY,
    batchId: '018f0000-0000-7000-8000-00000000b001',
    reason: 'nightly',
    orderedPracticeIds: ['018f0000-0000-7000-8000-000000000001'],
    requestId: null,
    supersededAt: null,
    createdAt: FIXTURE_INSTANT,
    updatedAt: FIXTURE_INSTANT,
    ...overrides,
  };
}

/**
 * An outcomes map in the reader's return shape. Values are validated against
 * the closed state vocabulary so a fixture cannot invent an outcome the
 * contract does not carry.
 */
export function buildOutcomesMap(
  entries: Record<string, PracticeOutcomeState>,
): ReadonlyMap<string, PracticeOutcomeState> {
  for (const [id, state] of Object.entries(entries)) {
    if (!PRACTICE_OUTCOME_STATES.includes(state)) {
      throw new Error(`journey fixtures: unknown outcome state '${state}' for ${id}`);
    }
  }
  return new Map(Object.entries(entries));
}
